/**
 * Crea tu organización — `OD-0015 D-02` paso 3, `OD-0028` (ACCEPTED).
 *
 * `GET /api/v1/me` responde 403 cuando el usuario no pertenece a ninguna
 * organización. Sus otros 403 —la empresa activa cerrada, o el acceso
 * desactivado al pasar a Free— traen motivo y van a `PantallaSinAcceso`.
 * Antes se mostraba "pídeselo a tu administrador", que es falso para quien
 * acaba de registrarse y no tiene administrador.
 *
 * Sólo se pide el nombre (`OD-0028 D-02`): la identidad fiscal es el paso 4 y
 * se puede diferir, para no poner un formulario fiscal antes de ver nada.
 *
 * Respuestas que no son éxito y qué se hace:
 *
 *   409  ya pertenece a una (p. ej. otra pestaña la creó)  -> se recarga la sesión
 *   401  la sesión caducó                                   -> vuelta a la entrada
 *   422  nombre no válido                                   -> error en el campo
 *
 * Un corte a medias se arregla reintentando: la API completa la MISMA
 * organización (`OD-0028 D-06`), así que el botón puede pulsarse otra vez sin
 * miedo a duplicar.
 */
import { useState } from "react";
import { api, ESTADO_VALIDACION } from "../api/cliente";
import { asesoriaPendiente } from "../enlaces/asesoria-del-enlace";
import { t } from "../i18n/es";
import { Aviso, Boton, Campo, Tarjeta } from "../ui/primitivas";

export function PantallaSinOrganizacion({
  hayConexion,
  onCreada,
  onSesionCaducada,
  onSalir,
}: {
  hayConexion: boolean;
  onCreada: () => void;
  onSesionCaducada: () => void;
  onSalir: () => void;
}) {
  const [nombre, setNombre] = useState("");
  const [errorNombre, setErrorNombre] = useState<string | null>(null);
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [creando, setCreando] = useState(false);
  const [saliendo, setSaliendo] = useState(false);

  async function crear(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErrorGeneral(null);
    if (nombre.trim() === "") {
      setErrorNombre(t.sinOrganizacion.faltaNombre);
      return;
    }
    setErrorNombre(null);

    setCreando(true);
    // OD-0063 D-08: el codigo del enlace de una gestoria, si se llego por uno.
    // Se lee al enviar: el correo de confirmacion abrio esta pestana sin el.
    const r = await api.crearOrganizacion(nombre.trim(), asesoriaPendiente(Date.now()));
    setCreando(false);

    // 409: ya tiene organización. No es un error para el usuario: se carga.
    if (r.ok || r.estado === 409) {
      onCreada();
      return;
    }
    if (r.estado === 0) setErrorGeneral(t.comun.sinRed);
    else if (r.estado === 401) onSesionCaducada();
    else if (r.estado === ESTADO_VALIDACION && r.error !== null) setErrorNombre(r.error.message);
    else setErrorGeneral(t.comun.errorGenerico);
  }

  async function salir(): Promise<void> {
    setSaliendo(true);
    await api.logout();
    setSaliendo(false);
    onSalir();
  }

  return (
    <Tarjeta titulo={t.sinOrganizacion.titulo}>
      <p>{t.sinOrganizacion.detalle}</p>
      <p className="ayuda-neutra">{t.sinOrganizacion.invitado}</p>
      <form onSubmit={crear} noValidate>
        <Campo
          id="organizacion-nombre"
          etiqueta={t.sinOrganizacion.nombre}
          valor={nombre}
          onChange={setNombre}
          autoComplete="organization"
          ayuda={t.sinOrganizacion.ayudaNombre}
          error={errorNombre}
          autoFocus
        />
        {errorGeneral !== null ? <Aviso tono="error">{errorGeneral}</Aviso> : null}
        {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
        <Boton tipo="submit" deshabilitado={creando || !hayConexion}>
          {creando ? t.sinOrganizacion.creando : t.sinOrganizacion.crear}
        </Boton>
      </form>
      <Boton variante="secundario" onClick={() => void salir()} deshabilitado={saliendo}>
        {saliendo ? t.cuenta.saliendo : t.cuenta.salir}
      </Boton>
    </Tarjeta>
  );
}
